<template>
  <div>
    <h5>Sublet Invoices</h5>
    <h6 class="text-uppercase">{{ title }}</h6>
    <span class="required-legend float-right p-0">* Required</span>
    <b-spinner v-if="loading" class="text-center"></b-spinner>

    <div v-else-if="!isPosted" class="sublet-inputs">
      <b-form-group label-class="col-head" label="File:" label-for="subletInvoiceFile">
        <b-form-file
          id="subletInvoiceFile"
          v-model="subletInvoiceFiles"
          class="d-flex"
          accept="application/pdf"
          placeholder="Choose sublet invoice .pdf file..."
          drop-placeholder="Drop file here..."
          :file-name-formatter="formatNames"
        ></b-form-file>
      </b-form-group>
      <b-form-group label-class="col-head" label="Description:" label-for="description">
        <b-input-group id="description">
          <b-form-input v-model="description" placeholder="Click to add description..."></b-form-input>
          <span class="required-asterisk">*</span>
        </b-input-group>
      </b-form-group>
      <b-form-group label-class="col-head" label="Operation Id:" label-for="operationId">
        <b-input-group id="operationId" class="w-50">
          <v-select v-model="opsId" :options="opsIdList" placeholder="Select Operation Id"></v-select>
          <span class="required-asterisk">*</span>
        </b-input-group>
      </b-form-group>
      <b-button
        :disabled="
          subletInvoiceFiles == null || description == null || description.trim() == '' || opsId == 0 || submitting
        "
        class="btn-primary"
        @click="submitInvoice"
      >
        <div v-show="submitting">
          <b-spinner small></b-spinner>
          submitting...
        </div>
        <div v-show="!submitting">Submit Invoice</div>
      </b-button>
    </div>
    <b-table class="mt-5" striped bordered :fields="fields" :items="subletInvoiceHistory">
      <template #cell(dateCreate)="{ item }">
        {{ item.dateCreate | dateTime }}

        <font-awesome-icon
          :id="`submit_tooltip_icon_${item.subletInvoiceId}`"
          icon="info-circle"
          class="mx-1 text-primary align-self-center"
          display="inline-block"
        />
        <b-tooltip
          :id="`submit_tooltip_${item.subletInvoiceId}`"
          :target="`submit_tooltip_icon_${item.subletInvoiceId}`"
          triggers="hover"
        >
          <div class="d-flex">
            <span>Added by&nbsp;</span>
            <employee-display :employee="item.employeeCreate" :show-email="false" />
          </div>
        </b-tooltip>
      </template>
      <template #cell(originalFilename)="{ item }">
        <b-link @click="downloadFile(item)">{{ item.originalFilename }}</b-link>
      </template>
    </b-table>
  </div>
</template>

<script>
// Components
import EmployeeDisplayComponent from '@/shared/components/ui/EmployeeDisplayComponent';
// Vuex
import { AttachmentGetters, AttachmentActions, AttachmentMutations } from '@/shared/store/attachment/types';
import { SubletInvoiceActions, SubletInvoiceGetters } from '@/shared/store/sublet-invoice/types';
import { LookupGetters } from '@/shared/store/lookup/types';
import { ServiceOrderGetters } from '@/shared/store/service-order/types';
import { mapGetters, mapActions, mapMutations } from 'vuex';
import vSelect from 'vue-select';
// helpers
import SuccessService from '@/shared/services/SuccessService';
import ErrorService from '@/shared/services/ErrorService';
import ExportService from '@/shared/services/ExportService';
// mixins
import UserAccessMixin from '@/shared/mixins/UserAccessMixin';
import UploadMixin from '@/shared/mixins/UploadMixin';
import { required } from 'vuelidate/lib/validators';

export default {
  name: 'SubletInvoice',
  components: {
    vSelect,
    'employee-display': EmployeeDisplayComponent
  },
  mixins: [UserAccessMixin, UploadMixin],
  props: {},
  data() {
    return {
      loading: false,
      uploadingFiles: false,
      subletInvoiceFiles: null,
      subletInvoices: [],
      description: '',
      opsId: 0,
      submitting: false
    };
  },
  validations: {
    message: {
      required
    }
  },
  computed: {
    ...mapGetters([
      AttachmentGetters.GET_ATTACHMENTS,
      AttachmentGetters.GET_WARRANTY_ASIDE_ATTACHMENTS,
      AttachmentGetters.GET_ATTACHMENTS_BY_KEY
    ]),
    ...mapGetters({
      supportedTypesList: LookupGetters.GET_SUPPORTED_TYPES_LIST,
      serviceOrder: ServiceOrderGetters.GET_SERVICE_ORDER,
      jobs: ServiceOrderGetters.GET_JOBS,
      subletInvoiceHistory: SubletInvoiceGetters.GET_SUBLET_INVOICE_HISTORY
    }),
    selectedFiles() {
      if (!this.subletInvoiceFiles) return '';
      return this.subletInvoiceFiles.map(x => x.name).join(', ');
    },
    attachments() {
      let attachments = this.storeKey
        ? this[AttachmentGetters.GET_ATTACHMENTS_BY_KEY](this.storeKey)
        : this[AttachmentGetters.GET_ATTACHMENTS];
      return attachments;
    },
    fields() {
      const sharedOptions = { tdClass: 'align-middle', sortable: true };
      const fields = [
        { key: 'dateCreate', label: 'Date/Time Submitted', ...sharedOptions },
        { key: 'originalFilename', label: 'File Name', ...sharedOptions },
        { key: 'description', label: 'Description', ...sharedOptions },
        { key: 'operationId', label: 'Job #', ...sharedOptions },
        { key: 'subletInvoiceStatusDescription', label: 'Status', ...sharedOptions }
      ];
      return fields;
    },
    serviceOrderId() {
      return this.$route.params.serviceOrderId;
    },
    unitId() {
      return this.$route.params.unitId;
    },
    opsIdList() {
      return Object.values(this.jobs).map(x => x.operationId);
    },
    isPosted() {
      return this.serviceOrder.invoiced;
    }
  },
  async created() {
    await this.fetch();
  },
  methods: {
    ...mapActions([
      SubletInvoiceActions.FETCH_SUBLET_INVOICE_HISTORY,
      AttachmentActions.FETCH_ATTACHMENTS,
      AttachmentActions.UPLOAD_ATTACHMENTS,
      SubletInvoiceActions.DOWNLOAD_SUBLET_INVOICE_HISTORY_PDF,
      AttachmentActions.UPDATE_ATTACHMENTS,
      AttachmentActions.REMOVE_ATTACHMENT,
      SubletInvoiceActions.SUBMIT_INVOICE
    ]),
    ...mapMutations([
      AttachmentMutations.SET_ATTACHMENT_DESCRIPTION,
      AttachmentMutations.SET_ATTACHMENT_INTERNAL_FLAG,
      AttachmentMutations.RESET_STATE
    ]),
    async fetch() {
      this.loading = true;
      try {
        await this[SubletInvoiceActions.FETCH_SUBLET_INVOICE_HISTORY]({
          serviceOrderId: this.serviceOrder.serviceOrderId
        });
      } catch {
        ErrorService.createErrorToast(this, 'Error fetching history.');
      } finally {
        this.loading = false;
      }
    },
    async downloadFile(invoice) {
      try {
        const byteArray = await this[SubletInvoiceActions.DOWNLOAD_SUBLET_INVOICE_HISTORY_PDF]({
          filename: invoice.filename
        });
        ExportService.downloadByteArray(byteArray, invoice.originalFilename, false);
      } catch (error) {
        ErrorService.unknownExportError(this);
      }
    },
    async updateAttachments() {
      if (
        !this.isWarrantySubmissionPage &&
        !this.isPartsRequestPage &&
        (this.attachments == null || this.attachments.length == 0)
      ) {
        ErrorService.createErrorToast(this, 'Upload attachments before updating.');
        return;
      }
      try {
        this.updatingAttachments = this.loading = true;
        await this[AttachmentActions.UPDATE_ATTACHMENTS]({
          key: this.storeKey,
          attachments: this.attachments,
          route: this.path
        });
        if (!this.isWarrantySubmissionPage && !this.isPartsRequestPage) {
          SuccessService.createSuccessToast(this.$root, `Updated attachments successfully.`);
        }
      } catch (error) {
        let errorMessage = 'Failed to update attachments.';
        ErrorService.createErrorToast(this, errorMessage);
      } finally {
        this.updatingAttachments = this.loading = false;
      }
    },
    async uploadAttachments() {
      await this.uploadFiles(this.subletInvoiceFiles, this.storeKey);
      this.subletInvoiceFiles = null;
    },
    async submitInvoice() {
      try {
        this.submitting = true;
        await this[SubletInvoiceActions.SUBMIT_INVOICE]({
          serviceOrderId: this.serviceOrderId,
          billToCustomerId: this.serviceOrder.billToCustomerId,
          jobId: this.opsId,
          description: this.description,
          unitId: this.unitId,
          files: this.subletInvoiceFiles
        });
        SuccessService.createSuccessToast(this.$root, `Submit Invoice successfully.`);
      } catch (error) {
        let errorMessage = 'Failed to submit invoices.';

        if (error.response && error.response.status == 415) {
          errorMessage = 'Unsupported file type. Sublet Invoices must be .pdf';
        }
        ErrorService.createErrorToast(this, errorMessage);
      } finally {
        this.submitting = false;
        this.subletInvoiceFiles = null;
        this.description = null;
        this.opsId = 0;
      }
    }
  }
};
</script>
<style>
.sublet-inputs {
  width: 75%;
}
</style>
